export type NotificationPermission = 'default' | 'granted' | 'denied';

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

export interface NotificationOptions {
  title: string;
  body?: string;
  icon?: string;
  badge?: string;
  image?: string;
  tag?: string;
  silent?: boolean;
  requireInteraction?: boolean;
  actions?: NotificationAction[];
  data?: any;
}

export interface PlatformInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  browser: string;
  supportsNotifications: boolean;
  supportsPush: boolean;
  supportsServiceWorker: boolean;
}

export class NotificationService {
  private static instance: NotificationService;
  private platformInfo: PlatformInfo;
  private permissionChangeCallbacks: Set<(permission: NotificationPermission) => void> = new Set();

  private constructor() {
    this.platformInfo = this.detectPlatform();
    this.setupPermissionListener();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private detectPlatform(): PlatformInfo {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    
    // Detect browser
    let browser = 'unknown';
    if (userAgent.includes('Chrome')) browser = 'chrome';
    else if (userAgent.includes('Firefox')) browser = 'firefox';
    else if (userAgent.includes('Safari')) browser = 'safari';
    else if (userAgent.includes('Edge')) browser = 'edge';

    return {
      isMobile,
      isIOS,
      isAndroid,
      browser,
      supportsNotifications: 'Notification' in window,
      supportsPush: 'PushManager' in window,
      supportsServiceWorker: 'serviceWorker' in navigator
    };
  }

  private setupPermissionListener(): void {
    // Some browsers support permission change events
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' }).then(permission => {
        permission.addEventListener('change', () => {
          this.notifyPermissionChange(this.getPermission());
        });
      }).catch(() => {
        // Permissions API not supported, fallback to polling
        this.setupPermissionPolling();
      });
    } else {
      this.setupPermissionPolling();
    }
  }

  private setupPermissionPolling(): void {
    let lastPermission = this.getPermission();
    setInterval(() => {
      const currentPermission = this.getPermission();
      if (currentPermission !== lastPermission) {
        lastPermission = currentPermission;
        this.notifyPermissionChange(currentPermission);
      }
    }, 1000);
  }

  private notifyPermissionChange(permission: NotificationPermission): void {
    this.permissionChangeCallbacks.forEach(callback => callback(permission));
  }

  public onPermissionChange(callback: (permission: NotificationPermission) => void): () => void {
    this.permissionChangeCallbacks.add(callback);
    return () => this.permissionChangeCallbacks.delete(callback);
  }

  public getPlatformInfo(): PlatformInfo {
    return { ...this.platformInfo };
  }

  public isSupported(): boolean {
    return this.platformInfo.supportsNotifications;
  }

  public getPermission(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission as NotificationPermission;
  }

  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Notifications are not supported on this platform');
    }

    // On iOS Safari, notifications can only be requested in response to user gesture
    if (this.platformInfo.isIOS && this.platformInfo.browser === 'safari') {
      console.info('iOS Safari detected: Ensure this is called from a user gesture');
    }

    const permission = await Notification.requestPermission();
    this.notifyPermissionChange(permission as NotificationPermission);
    return permission as NotificationPermission;
  }

  public async showNotification(options: NotificationOptions): Promise<Notification | null> {
    const permission = this.getPermission();
    
    if (permission !== 'granted') {
      console.warn('Cannot show notification: permission not granted');
      return null;
    }

    if (!this.isSupported()) {
      console.warn('Cannot show notification: not supported on this platform');
      return null;
    }

    try {
      // Platform-specific adjustments
      const adjustedOptions = this.adjustOptionsForPlatform(options);
      
      const notification = new Notification(options.title, adjustedOptions);
      
      // Set up auto-close for mobile platforms to save battery
      if (this.platformInfo.isMobile && !adjustedOptions.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return notification;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return null;
    }
  }

  private adjustOptionsForPlatform(options: NotificationOptions): NotificationOptions {
    const adjusted = { ...options };

    // iOS Safari has limited support for notification features
    if (this.platformInfo.isIOS && this.platformInfo.browser === 'safari') {
      // iOS Safari doesn't support actions, images, or custom sounds
      delete adjusted.actions;
      delete adjusted.image;
      delete adjusted.badge;
    }

    // Android Chrome supports most features
    if (this.platformInfo.isAndroid && this.platformInfo.browser === 'chrome') {
      // Android supports vibration patterns
      if (!adjusted.silent && 'vibrate' in navigator) {
        (adjusted as any).vibrate = [200, 100, 200];
      }
    }

    // Firefox has some limitations
    if (this.platformInfo.browser === 'firefox') {
      // Firefox doesn't support actions in all versions
      if (adjusted.actions && adjusted.actions.length > 0) {
        console.warn('Firefox may not support notification actions');
      }
    }

    return adjusted;
  }

  public async showTaskReminder(taskTitle: string, dueTime?: string): Promise<Notification | null> {
    const options: NotificationOptions = {
      title: 'Task Reminder',
      body: dueTime ? `${taskTitle} is due ${dueTime}` : taskTitle,
      icon: '/icons/icon-192x192.png',
      badge: '/icons/badge-72x72.png',
      tag: 'task-reminder',
      requireInteraction: true,
      actions: [
        { action: 'complete', title: 'Mark Complete' },
        { action: 'snooze', title: 'Snooze 10min' }
      ],
      data: { type: 'task-reminder', taskTitle, dueTime }
    };

    return this.showNotification(options);
  }

  public async showFocusSessionComplete(sessionDuration: number): Promise<Notification | null> {
    const options: NotificationOptions = {
      title: 'Focus Session Complete! 🎉',
      body: `Great job! You focused for ${Math.round(sessionDuration / 60)} minutes.`,
      icon: '/icons/icon-192x192.png',
      badge: '/icons/badge-72x72.png',
      tag: 'focus-complete',
      requireInteraction: false,
      actions: [
        { action: 'break', title: 'Take Break' },
        { action: 'continue', title: 'Continue Working' }
      ],
      data: { type: 'focus-complete', sessionDuration }
    };

    return this.showNotification(options);
  }

  public async showBreakReminder(): Promise<Notification | null> {
    const options: NotificationOptions = {
      title: 'Break Time!',
      body: 'Time to take a break and recharge.',
      icon: '/icons/icon-192x192.png',
      badge: '/icons/badge-72x72.png',
      tag: 'break-reminder',
      requireInteraction: false,
      silent: false,
      data: { type: 'break-reminder' }
    };

    return this.showNotification(options);
  }

  public async testNotification(): Promise<Notification | null> {
    const options: NotificationOptions = {
      title: 'Test Notification',
      body: 'This is a test notification from Flowcus.',
      icon: '/icons/icon-192x192.png',
      tag: 'test',
      requireInteraction: false,
      data: { type: 'test' }
    };

    return this.showNotification(options);
  }

  public getStatusMessage(): string {
    if (!this.isSupported()) {
      return 'Notifications are not supported on this device/browser';
    }

    const permission = this.getPermission();
    switch (permission) {
      case 'granted':
        return 'Notifications are enabled';
      case 'denied':
        return 'Notifications are blocked. Enable them in your browser settings.';
      case 'default':
        return 'Click to enable notifications';
      default:
        return 'Unknown notification status';
    }
  }

  public getBrowserSpecificInstructions(): string[] {
    const instructions: string[] = [];

    if (!this.isSupported()) {
      instructions.push('Your browser does not support notifications.');
      return instructions;
    }

    const { browser, isMobile, isIOS } = this.platformInfo;

    if (isIOS) {
      instructions.push('On iOS, notifications work best when you add Flowcus to your home screen.');
      if (browser === 'safari') {
        instructions.push('In Safari, tap the share button and select "Add to Home Screen".');
      }
    }

    if (browser === 'chrome') {
      instructions.push('Chrome supports rich notifications with actions and images.');
      if (isMobile) {
        instructions.push('Enable notifications in Chrome settings for the best experience.');
      }
    }

    if (browser === 'firefox') {
      instructions.push('Firefox supports basic notifications.');
      instructions.push('Some advanced features may not be available.');
    }

    if (browser === 'safari' && !isIOS) {
      instructions.push('Safari on macOS supports notifications after permission is granted.');
    }

    if (this.getPermission() === 'denied') {
      instructions.push('To re-enable notifications:');
      if (browser === 'chrome') {
        instructions.push('1. Click the lock icon in the address bar');
        instructions.push('2. Change notifications to "Allow"');
      } else if (browser === 'firefox') {
        instructions.push('1. Click the shield icon in the address bar');
        instructions.push('2. Change notification permissions');
      } else if (browser === 'safari') {
        instructions.push('1. Go to Safari > Settings > Websites > Notifications');
        instructions.push('2. Change the setting for this website');
      }
    }

    return instructions;
  }

  public clearAllNotifications(): void {
    // Note: There's no direct way to clear all notifications
    // This is a limitation of the Notifications API
    // New notifications will replace old ones with the same tag
    console.info('Notifications cleared (new notifications will replace old ones with same tag)');
  }
}

// Export a singleton instance
export const notificationService = NotificationService.getInstance();